import { uniq } from 'lodash';
import { GDPR_REGIONS } from '../constants/regions';
import { normalizeRegion } from './gdpr-utils';
import { isParentTenant } from './tenant-type-utils';
import { isShowHidden } from './router-utils';
import { TENANT_TYPES } from '../constants/tenant-types';
import { MAIN_BRAND_NAME } from '../constants/user-team-ids';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import * as ROUTES from '../constants/routes';
import * as FEATURES from '../constants/features';
import translate, { sharedFeatures } from '../translate';
import { safeJsonParse } from './general';
import * as TenantUtils from './tenant-type-utils';

const t = (key, params) => translate(`features.${key}`, params);

// CAASM should be available only in the US region
const CAASM_ALLOWED_REGIONS = [GDPR_REGIONS.US];

export const getCustomerFeatures = ({
  customer,
  customerId,
  isCyberintUser,
  isCyberintRootAdmin,
  flags,
  customerConfig = {}
}) => {
  const { tenant_type: tenantType } = customer;
  const { isSalesforceActive } = customerConfig;
  const isCustomerHasInfinityTenants = customer?.mapped_infinity_tenants?.length > 0;

  const isMssp = TenantUtils.isMsspTenant(customer);

  const flagRegions = safeJsonParse(flags?.[FEATURE_FLAGS.CAASM_ALLOWED_REGIONS])?.regions;
  const allowedRegions = Array.isArray(flagRegions) ? flagRegions.map(r => r.toLowerCase()) : CAASM_ALLOWED_REGIONS;
  const isCaasmDisabled = !allowedRegions.includes(normalizeRegion(customer.region));

  return [
    {
      featureName: t('title.tem'),
      summary: t('summary.tem'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.temAccess'),
              summary: t('summary.temAccess'),
              featureValues: [FEATURES.TEM_ACCESS]
            },
            {
              featureName: t('title.temMap'),
              summary: t('summary.temMap'),
              featureValues: [FEATURES.TEM_LIVE_ATTACK_MAP]
            }
          ]
        }
      ]
    },
    {
      featureName: t('title.alerts'),
      summary: t('summary.alerts'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.alertsView'),
              summary: t('summary.alertsView'),
              featureValues: [FEATURES.ALERTS_VIEW, FEATURES.VIEW_EXECUTIVE_DASHBOARDS, FEATURES.ALERTS_NOTIFICATIONS]
            },
            {
              featureName: t('title.in_review'),
              summary: t('summary.in_review'),
              featureValues: [FEATURES.ALERTS_CREATE]
            },
            ...(isParentTenant(tenantType)
              ? [
                  {
                    featureName: t('title.alert_clone'),
                    summary: t('summary.alert_clone'),
                    featureValues: [FEATURES.ALERTS_CLONE]
                  }
                ]
              : []),
            {
              featureName: t('title.aiAlertSummary'),
              summary: t('summary.aiAlertSummary'),
              featureValues: [FEATURES.AI_ALERT_SUMMARY]
            }
          ]
        },
        {
          title: t('autoAlerts.asmAutoAlerts'),
          children: [
            {
              featureName: t('title.exploitable-ports'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_EXPLOITABLE_PORT]
            },
            {
              featureName: t('title.corporate_credentials_exposed'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_CORP_CREDENTIALS_EXPOSED]
            },
            {
              featureName: t('title.third_party_credentials_exposed'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_3RD_PARTY_CREDENTIALS_EXPOSED]
            },
            {
              featureName: t('title.automatic-hijackable-subdomain'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_HIJACKABLE_SUBDOMAIN]
            },
            {
              featureName: t('title.automatic-exposed-cloud-storage'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_EXPOSED_CLOUD_STORAGE]
            },
            {
              featureName: t('title.automatic-exposed-web-interface'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_EXPOSED_WEB_INTERFACE]
            },
            {
              featureName: t('title.automatic-email_security-missing_spf'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MISSING_COMPANY_DOMAIN_SPF_RECORDS]
            },
            {
              featureName: t('title.automatic-email_security-missing_dmarc'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MISSING_COMPANY_DOMAIN_DMARC_RECORDS]
            },
            {
              featureName: t('title.automatic-missing_caa'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MISSING_COMPANY_DOMAIN_CAA_RECORDS]
            },
            {
              featureName: t('title.automatic-company_mail_server_blocklisted'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_COMPANY_MAIL_SERVER_BLOCKLISTED]
            },
            {
              featureName: t('title.automatic-vulnerable_technology_on_asset'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_VULNERABLE_TECHNOLOGY_ON_ASSET],
              summary: t('summary.cveIntelAndTechDiscoveryShouldBeOnForAutomation')
            },
            {
              featureName: t('title.automatic-vulnerable_technology_detected_on_manually_added_technology'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_VULNERABLE_TECHNOLOGY_ON_MANUALLY_ADDED_TECHNOLOGY]
            },
            {
              featureName: t('title.exploitable_vulnerability_found_on_company_asset'),
              featureValues: [FEATURES.AUTOMATIC_ALERT_EXPLOITABLE_VULNERABILITY_FOUND_ON_COMPANY_ASSET]
            },
            {
              featureName: t('title.automatic-ssl_tls_certificate_expiry'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_SSL_CERTIFICATE_EXPIRE]
            },
            {
              featureName: t('title.automatic-exploit_found_on_company_asset'),
              featureValues: [FEATURES.AUTOMATIC_ALERT_EXPLOIT_FOUND_ON_COMPANY_ASSET]
            },
            {
              featureName: t('title.automatic-tls_no_forward_secrecy'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_NO_FORWARD_SECRECY]
            },
            {
              featureName: t('title.automatic-tls_latest_version_not_supported'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_LATEST_VERSION_NOT_SUPPORTED]
            },
            {
              featureName: t('title.automatic-tls_outdated_version_ssl_tls_supported'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_OUTDATED_VERSION_SSL_TLS_SUPPORTED]
            },
            {
              featureName: t('title.automatic-tls_server_has_known_vulnerability'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_SERVER_HAS_KNOWN_VULNERABILITY]
            },
            {
              featureName: t('title.automatic-tls_insecure_cipher_suites'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_INSECURE_CIPHER_SUITES]
            },
            {
              featureName: t('title.automatic-tls_weak_cipher_suites'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_WEAK_CIPHER_SUITES]
            },
            {
              featureName: t('title.automatic-tls_server_accepts_insecure_renegotiation'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_TLS_SERVER_ACCEPTS_INSECURE_RENEGOTIATION]
            },
            {
              featureName: t('title.automatic-web_app_security_missing_hsts_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_HSTS_HEADER_DETECTED]
            },
            {
              featureName: t('title.automatic-web_app_security_misconfigured_hsts_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISCONFIGURED_HSTS_HEADER_DETECTED]
            },
            {
              featureName: t('title.automatic-web_app_security_missing_x_frame_options_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_X_FRAME_OPTIONS_HEADER_DETECTED]
            },
            {
              featureName: t('title.automatic-web_app_security_misconfigured_x_frame_options_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISCONFIGURED_X_FRAME_OPTIONS_HEADER_DETECTED]
            },
            {
              featureName: t('title.automatic-web_app_security_missing_x_content_type_options_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_X_CONTENT_TYPE_OPTIONS_HEADER_DETECTED]
            },
            {
              featureName: t('title.automatic-web_app_security_misconfigured_x_content_type_options_header_detected'),
              featureValues: [
                FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISCONFIGURED_X_CONTENT_TYPE_OPTIONS_HEADER_DETECTED
              ]
            },
            {
              featureName: t('title.automatic-web_app_security_missing_content_security_policy_header_detected'),
              featureValues: [
                FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_CONTENT_SECURITY_POLICY_HEADER_DETECTED
              ]
            },
            {
              featureName: t('title.automatic-web_app_security_missing_referrer_policy_header_detected'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_REFERRER_POLICY_HEADER_DETECTED]
            },
            {
              featureName: t(
                'title.automatic-web_app_security_missing_x_permitted_cross_domain_policies_header_detected'
              ),
              featureValues: [
                FEATURES.AUTOMATIC_ALERTS_WEB_APP_SECURITY_MISSING_X_PERMITTED_CROSS_DOMAIN_POLICIES_HEADER_DETECTED
              ]
            }
          ]
        },

        {
          title: t('autoAlerts.tiAutomaticAlerts'),
          children: [
            {
              featureName: t('title.customer-credentials'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_CUSTOMER_CREDENTIALS]
            },
            {
              featureName: t('title.automatic-payment-cards'),
              featureValues: [FEATURES.AUTOMATIC_PAYMENT_CARDS_EXPOSED]
            },
            {
              featureName: t('title.automatic-exposed-access-token'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_EXPOSED_ACCESS_TOKEN]
            },
            {
              featureName: t('title.automatic-employee-machine-infected-by-malware'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_INFECTED_MACHINE]
            },
            {
              featureName: t('title.malicious-file-targeting-employees'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MALICIOUS_FILE_TARGETING_EMPLOYEES]
            },
            {
              featureName: t('title.internal-email-correspondence'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_INTERNAL_EMAIL_CORRESPONDENCE]
            },
            {
              featureName: t('title.company-confidential-file-exposed'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_COMPANY_CONFIDENTIAL_FILE_EXPOSED]
            },
            {
              featureName: t('title.breached-data-potentially-related-to-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_BREACHED_DATA_POTENTIALLY_RELATED_TO_COMPANY]
            },
            {
              featureName: t('title.automatic-mobile_app_distributed_unofficially'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MOBILE_APP_DISTRIBUTED_UNOFFICIALLY]
            },
            {
              featureName: t('title.automatic-mobile_app_impersonating_company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_MOBILE_APP_IMPERSONATING_COMPANY]
            },
            {
              featureName: t('title.company-source-code-exposed'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_COMPANY_SOURCE_CODE_EXPOSED]
            },
            {
              featureName: t('title.customer-credentials-for-sale'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_CUSTOMER_CREDENTIALS_OFFERED_FOR_SALE]
            },
            {
              featureName: t('title.employee-credentials-for-sale'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_EMPLOYEE_CREDENTIALS_OFFERED_FOR_SALE]
            },
            {
              featureName: t('title.credential-stuffing-tool-targeting-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_CREDENTIAL_STUFFING_TARGETING_COMPANY]
            }
          ]
        },

        {
          title: t('autoAlerts.drp'),
          children: [
            {
              featureName: t('title.automatic-phishing-alert'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_PHISHING_WEBSITE]
            },
            {
              featureName: t('title.automatic-lookalike-domain-alert'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_LOOKALIKE_DOMAIN]
            },
            {
              featureName: t('title.alerts.automatic.social_media_account_impersonating_company'),
              featureValues: [FEATURES.AUTOMATIC_SM_ACCOUNT_IMPERSONATING_COMPANY]
            },
            {
              featureName: t('title.alerts.automatic.social_media_account_impersonating_company_executive'),
              featureValues: [FEATURES.AUTOMATIC_SM_ACCOUNT_IMPERSONATING_COMPANY_EXECUTIVE]
            }
          ]
        },
        {
          title: t('categories.supplyChainAutomaticAlerts'),
          children: [
            {
              featureName: t('title.supplyChainRansomwareAttackTargetingThirdPartyVendor'),
              summary: t('summary.applicableForPremiumVendorsOnly'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_RANSOMWARE_ATTACK_TARGETING_THIRD_PARTY_VENDOR]
            },
            {
              featureName: t('title.supplyChainThirdPartyVendorBreached'),
              summary: t('summary.applicableForPremiumVendorsOnly'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_THIRD_PARTY_VENDOR_BREACHED]
            }
          ]
        },
        {
          title: t('title.experimental-automatic-alerts'),
          children: [
            {
              featureName: t('title.customer-payment-cards-for-sale'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_CUSTOMER_PAYMENT_CARDS_OFFERED_FOR_SALE]
            },
            {
              featureName: t('title.ransomware-attack-targeting-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_RANSOMWARE_ATTACK_TARGETING_COMPANY]
            },
            {
              featureName: t('title.fraudulent-refund-services-targeting-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_FRAUDULENT_REFUND_SERVICES_TARGETING_COMPANY]
            },
            {
              featureName: t('title.fraudulent-refund-tutorial-targeting-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_FRAUDULENT_REFUND_TUTORIAL_TARGETING_COMPANY]
            },
            {
              featureName: t('title.phishing-kit-targeting-company'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_PHISHING_KIT_TARGETING_COMPANY]
            },
            {
              featureName: t('title.data-scraping-tool-for-company-application'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_DATA_SCRAPING_TOOL_FOR_COMPANY_APPLICATION]
            },
            {
              featureName: t('title.fraudulent-account-temp-phone'),
              featureValues: [FEATURES.AUTOMATIC_ALERTS_FRAUDULENT_ACCOUNT_TEMP_PHONE]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.asm'),
      summary: t('summary.asm'),
      featureValues: [],
      categories: [
        {
          title: '',
          children: [
            {
              featureName: t('title.asmView'),
              summary: t('summary.asmView'),
              featureValues: [FEATURES.ASM_FEATURE_VIEW]
            },
            {
              featureName: t('title.assetDiscovery'),
              summary: t('summary.assetDiscovery'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_ASSET_DISCOVERY]
            },
            {
              featureName: t('title.issueScanning'),
              summary: t('summary.issueScanning'),
              featureValues: [
                FEATURES.ATTACK_SURFACE_MONITORING_ISSUE_DETECTION,
                FEATURES.ATTACK_SURFACE_MONITORING_SCORE_CALCULATION
              ]
            },
            {
              featureName: t('title.autoCloseAlert'),
              summary: t('summary.autoCloseAlert'),
              featureValues: [FEATURES.AUTO_CLOSE_ALERT_FEATURE]
            },
            {
              featureName: t('title.scheduledDiscovery'),
              summary: t('summary.scheduledDiscovery'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_FULL_SCHEDULED_DISCOVERY]
            },

            {
              featureName: t('title.onGoingScan'),
              summary: t('summary.onGoingScan'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_IN_SCOPE_SCAN]
            },
            {
              featureName: t('title.validation'),
              summary: t('summary.validation'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_VALIDATION]
            },
            {
              featureName: t('title.licenseView'),
              summary: t('summary.licenseView'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_LICENSE_VIEW]
            },
            {
              featureName: t('title.technologyDiscovery'),
              summary: t('summary.technologyDiscovery'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_TECHNOLOGY_DISCOVERY]
            },
            {
              featureName: t('title.manualTechnologyAddition'),
              summary: t('summary.manualTechnologyAddition'),
              featureValues: [FEATURES.SUPPLY_CHAIN_TECH_INVENTORY_MANAGE_MANUAL]
            },

            {
              featureName: t('title.securityPostureScoreCalculation'),
              summary: t('summary.securityPostureScoreCalculation'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_SECURITY_POSTURE_SCORE_CALCULATION]
            },
            {
              featureName: t('title.activeVulnerabilityScan'),
              summary: t('summary.activeVulnerabilityScan'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_ACTIVE_VULNERABILITY_SCAN],
              disabled: isSalesforceActive,
              disclaimer: isMssp ? t('disclaimers.msspAev') : t('disclaimers.aev')
            },
            {
              featureName: t('title.scheduledActiveVulnerabilityScan'),
              summary: t('summary.scheduledActiveVulnerabilityScan'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_FULL_SCHEDULED_ACTIVE_VULNERABILITY_SCAN],
              disabled: isSalesforceActive
            },
            {
              featureName: t('title.automaticAevAlertsValidation'),
              summary: t('summary.automaticAevAlertsValidation'),
              featureValues: FEATURES.AEV_VALIDATION_FEATURES
            },
            {
              featureName: t('title.quantumIntegration'),
              summary: t('summary.quantumIntegration'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_INTEGRATIONS_QUANTUM],
              disclaimer: isCustomerHasInfinityTenants ? null : t('disclaimers.quantumIntegration')
            },
            {
              featureName: t('title.caasmIntegration'),
              summary: t('summary.caasmIntegration'),
              disabled: isCaasmDisabled,
              disabledReason: translate('common.featureNotAvailableInRegion'),
              featureValues: [FEATURES.ATTACK_SURFACE_MONITORING_INTEGRATIONS_CAASM]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.caasmAccess'),
      summary: t('summary.caasmAccess'),
      featureValues: [FEATURES.CAASM_ACCESS],
      categories: [],
      disabled: isCaasmDisabled,
      disabledReason: translate('common.featureNotAvailableInRegion')
    },
    {
      featureName: t('title.rawIntel'),
      summary: t('summary.rawIntel'),
      featureValues: [],
      categories: [
        {
          title: '',
          children: [
            {
              featureName: t('title.rawIntelDataCollection'),
              summary: t('summary.rawIntelDataCollection'),
              featureValues: FEATURES.RAW_INTEL_DATA_COLLECTION_FEATURES
            },
            {
              featureName: t('title.home'),
              summary: t('summary.home'),
              featureValues: [FEATURES.HOME_FEATURE]
            },
            {
              featureName: t('title.generateCustomedAlertRawIntel'),
              summary: t('summary.generateCustomedAlertRawIntel'),
              featureValues: [FEATURES.SAVED_QUERY_RAW_INTEL_MONITOR]
            },
            {
              featureName: t('title.aiSummary'),
              summary: t('summary.aiSummary'),
              featureValues: [FEATURES.AI_SUMMARY]
            },
            {
              featureName: t('title.iocView'),
              summary: t('summary.iocView'),
              featureValues: [FEATURES.IOC_VIEW]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.digitalRiskProtection'),
      summary: t('summary.digitalRiskProtection'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.phishing'),
              summary: t('summary.phishing'),
              featureValues: FEATURES.PHISHING_DETECTION_FEATURES
            },
            {
              featureName: t('title.socialMediaCrawler'),
              summary: t('summary.socialMediaCrawler'),
              featureValues: [
                FEATURES.SM_PAGE_FACEBOOK,
                FEATURES.SM_PAGE_INSTAGRAM,
                FEATURES.SM_PAGE_TIKTOK,
                FEATURES.SM_PAGE_TWITTER,
                FEATURES.SM_PAGE_LINKEDIN
              ]
            },
            {
              featureName: t('title.socialMediaView'),
              summary: t('title.socialMediaView'),
              featureValues: [FEATURES.SOCIAL_MEDIA_FAKE_PAGES]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.remediationAndInvestigation'),
      summary: t('summary.remediationAndInvestigation'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.takedowns'),
              summary: t('summary.takedowns'),
              featureValues: [FEATURES.TAKEDOWN_SUBMIT]
            },
            {
              featureName: t('title.takedownsNotifications'),
              summary: t('summary.takedownsNotifications'),
              featureValues: [FEATURES.TAKEDOWN_NOTIFICATION]
            },
            {
              featureName: t('title.credsPurchased'),
              summary: t('summary.credsPurchased'),
              featureValues: [FEATURES.CREDENTIAL_PURCHASE_ACCESS]
            },
            {
              featureName: t('title.credsNotifications'),
              summary: t('summary.credsNotifications'),
              featureValues: [FEATURES.CREDENTIAL_PURCHASE_NOTIFICATIONS]
            },
            {
              featureName: t('title.investigation'),
              summary: t('summary.investigation'),
              featureValues: [FEATURES.INVESTIGATION_ACCESS]
            }
            // TODO: We will use it later.
            // {
            //   featureName: t('title.investigation_notifications'),
            //   summary: t('summary.investigation_notifications'),
            //   featureValues: [INVESTIGATION_NOTIFICATIONS]
            // }
          ]
        }
      ]
    },

    {
      featureName: t('title.reportsNotification'),
      summary: t('summary.reportsNotification'),
      featureValues: [FEATURES.REPORTS_NOTIFICATIONS],
      categories: []
    },

    {
      featureName: t('title.threatHunting'),
      summary: t('summary.threatHunting'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.intelDataLake'),
              summary: t('summary.intelDataLake'),
              featureValues: [FEATURES.INTELLIGENCE_ARCHIVE_VIEW, FEATURES.GLOBAL_SEARCH]
            },
            {
              featureName: t('title.generateCustomizedAlertsDataLake'),
              summary: t('summary.generateCustomizedAlertsDataLake'),
              featureValues: [FEATURES.SAVED_QUERY_DATA_LAKE_MONITOR]
            },
            {
              featureName: t('title.forensics'),
              summary: t('summary.forensics'),
              featureValues: [FEATURES.TAB_FORENSICS_FEATURE, FEATURES.TAB_FORENSICS_QUICK_INVESTIGATION_FEATURE],
              disabled: isSalesforceActive
            },
            {
              featureName: t('title.cve'),
              summary: t('summary.cve'),
              featureValues: [FEATURES.CVE_ACCESS],
              disabled: isSalesforceActive
            },
            {
              featureName: t('title.panoptes'),
              summary: t('summary.panoptes'),
              featureValues: [FEATURES.AI_PANOPTES]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.supplyChainIntelligence'),
      summary: t('summary.supplyChainIntelligence'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.supplyChainIntelligenceModule'),
              summary: t('summary.supplyChainIntelligence'),
              featureValues: [FEATURES.SUPPLY_CHAIN_VENDOR_ACCESS]
            }
          ]
        }
      ]
    },

    {
      featureName: t('title.assetMgmt'),
      summary: t('summary.assetMgmt'),
      featureValues: [],
      categories: [
        {
          title: null,
          children: [
            {
              featureName: t('title.assetCreate'),
              summary: t('summary.assetCreate'),
              featureValues: [FEATURES.ASSET_MGMT_CREATE]
            },
            ...(isCyberintUser
              ? [
                  {
                    featureName: t('title.asset_grouping_view'),
                    summary: t('summary.asset_grouping_view'),
                    featureValues: [FEATURES.ASSET_GROUPING_VIEW]
                  }
                ]
              : [])
          ]
        }
      ]
    },

    ...(isCyberintUser && customerId !== MAIN_BRAND_NAME
      ? [
          {
            featureName: t('title.multi_tenant'),
            summary: null,
            featureValues: [],
            categories: [
              {
                title: null,
                children: [
                  {
                    featureName: t('title.msspSummaryView'),
                    summary: t('summary.msspSummaryView'),
                    featureValues: [FEATURES.ENV_SUMMARY_FEATURE_VIEW]
                  },
                  ...(isParentTenant(tenantType)
                    ? [
                        {
                          featureName: t('title.unified_alerts_view'),
                          summary: t('summary.unified_alerts_view'),
                          featureValues: [FEATURES.MSSP_ALERT_CENTER]
                        }
                      ]
                    : [])
                ]
              }
            ]
          }
        ]
      : []),
    ...(isCyberintUser
      ? [
          {
            featureName: t('title.advanced'),
            summary: null,
            featureValues: [],
            categories: [
              {
                title: t('title.leaked_credentials'),
                children: [
                  {
                    featureName: t('title.api_leaked_credentials'),
                    summary: t('summary.api_leaked_credentials'),
                    featureValues: [FEATURES.API_LEAKED_CREDENTIALS]
                  },
                  {
                    featureName: t('title.api_leaked_credentials_broad_query'),
                    summary: t('summary.api_leaked_credentials_broad_query'),
                    featureValues: [FEATURES.API_LEAKED_CREDENTIALS_BROAD_QUERY]
                  },
                  {
                    featureName: t('title.compromised_creds'),
                    featureValues: [FEATURES.FEEDS_CREDENTIALS]
                  }
                ]
              }
            ]
          }
        ]
      : []),
    ...(isCyberintRootAdmin
      ? [
          {
            featureName: t('title.leaked_credentials_screen'),
            summary: t('summary.leaked_credentials_screen'),
            featureValues: [FEATURES.COMPROMISED_CREDENTIALS_ACCESS],
            categories: []
          }
        ]
      : []),
    ...(isCyberintUser && isShowHidden()
      ? [
          {
            featureName: t('title.newLicenseModel'),
            summary: t('summary.newLicenseModel'),
            featureValues: [FEATURES.LICENSE_TENANT_TYPES],
            categories: []
          }
        ]
      : [])
  ];
};

const getChildFeatures = list =>
  list?.reduce((acc, item) => {
    const myFeatures = item.featureValues?.reduce((fs, f) => ({ ...fs, [f]: item.featureName }), {});
    const aggChildren = item.categories?.reduce((arr, cat) => [...arr, ...cat.children], []);
    const childFeatures = getChildFeatures(aggChildren);
    return { ...acc, ...myFeatures, ...childFeatures };
  }, {});

const CUSTOMER_FEATURES = getChildFeatures(
  getCustomerFeatures({ customer: { tenant_type: TENANT_TYPES.MSSP_MANAGEMENT }, customerId: '', isCyberintUser: true })
);

const ASSET_FEATURES = Object.entries(sharedFeatures.asset_features).reduce(
  (acc, [key, val]) => ({ ...acc, [key]: val.label }),
  {}
);

const UNMAPPED_FEATURES = {
  [FEATURES.ASM_FEATURE_MONITOR]: t('title.asmMonitor')
};

/** FF to name */
export const FEATURE_NAMES = { ...UNMAPPED_FEATURES, ...CUSTOMER_FEATURES, ...ASSET_FEATURES };

export const NAME_TO_FEATURES = Object.entries(FEATURE_NAMES).reduce((acc, [key, val]) => {
  return { ...acc, [val]: [...(acc[val] || []), key] };
}, {});

const THREAT_HUNTING_FEATURE_NAMES = [
  t('title.intelDataLake'),
  t('title.generateCustomizedAlertsDataLake'),
  t('title.forensics')
];

export const THREAT_HUNTING_FEATURES = THREAT_HUNTING_FEATURE_NAMES.flatMap(name => NAME_TO_FEATURES[name]);

export const TENANT_TYPE_TO_FORCED_ENABLED_FEATURE_NAMES = {
  [TENANT_TYPES.CYBERINT_MANAGEMENT]: [...THREAT_HUNTING_FEATURE_NAMES],
  [TENANT_TYPES.CYBERINT_PROVISIONING]: [...THREAT_HUNTING_FEATURE_NAMES],
  [TENANT_TYPES.CYBERINT_CUSTOMER]: [...THREAT_HUNTING_FEATURE_NAMES],
  [TENANT_TYPES.MSSP_MANAGEMENT]: [...THREAT_HUNTING_FEATURE_NAMES],
  [TENANT_TYPES.MULTI_TENANT_MANAGEMENT]: [...THREAT_HUNTING_FEATURE_NAMES]
};
export const TENANT_TYPE_TO_FORCED_ENABLED_FEATURES = {
  [TENANT_TYPES.CYBERINT_MANAGEMENT]: [...THREAT_HUNTING_FEATURES],
  [TENANT_TYPES.CYBERINT_PROVISIONING]: [...THREAT_HUNTING_FEATURES],
  [TENANT_TYPES.CYBERINT_CUSTOMER]: [...THREAT_HUNTING_FEATURES],
  [TENANT_TYPES.MSSP_MANAGEMENT]: [...THREAT_HUNTING_FEATURES],
  [TENANT_TYPES.MULTI_TENANT_MANAGEMENT]: [...THREAT_HUNTING_FEATURES]
};

const { ERM_DOMAINS, TEM_DOMAINS, CAASM_DOMAINS } = ROUTES;

const hasAll = (a1, a2) => {
  if (!a2?.length || !a1.length) return false;
  return a2.every(f => a1.includes(f));
};

export const getFeatureDomains = ({ features }) => {
  const domains = [];
  const defFF = [...FEATURES.DEFAULT_WITH_AUTO_FEATURES, ...THREAT_HUNTING_FEATURES];
  let remainingFeatures = features.filter(f => !defFF.includes(f));
  if (hasAll(features, FEATURES.TEM_MINIMAL_FEATURES) || hasAll(features, FEATURES.TEM_VISIBLE_FEATURES)) {
    domains.push(...TEM_DOMAINS);
    remainingFeatures = remainingFeatures.filter(f => !FEATURES.ALL_TEM_FEATURES.includes(f));
  }
  if (hasAll(features, FEATURES.CAASM_FEATURES)) {
    // what if CAASM_FEATURES have mutual features with ALL_TEM_FEATURES - that's why we check against features not remainingFeatures
    domains.push(...CAASM_DOMAINS);
    remainingFeatures = remainingFeatures.filter(f => !FEATURES.CAASM_FEATURES.includes(f));
  }
  /*
  if (hasSome(FEATURES.ERM_FEATURES, features)) {
    domains.push(...ERM_DOMAINS);
  }
  */
  if (remainingFeatures.length > 0 || domains.length === 0) {
    domains.push(...ERM_DOMAINS);
  }

  return uniq(domains);
};

/**
 * @param domains - the tab or route supported domains
 * @param features - the selected tenant features to compute the allowedDomains based on
 * @param allowedDomains - the selected tenant allowedDomains (computed based on features)
 * */
export const isAllowedDomains = ({ domains, features, allowedDomains }) => {
  if (!domains || !features) return true;
  const _allowedDomains = allowedDomains || getFeatureDomains({ features });
  // const allowedDomains = getFeatureDomainsFromTenantTypeOrEngagement({ tenant });
  return domains.some(d => _allowedDomains.includes(d));
};
